import type {
	ApplicationDiscount,
	COUPON_DISCOUNT,
	FEE_GRID_RESPONSE,
	FEE_ITEM,
	FIELD,
	PaymentStatus,
} from "@/types/form-types"
import { createContext, useContext } from "react"
import type { UseFormReturn } from "react-hook-form"
import type { z, ZodSchema } from "zod"
import type {
	combinedSchema,
	loanOfflinePaymentSchema,
	offlinePaymentSchema,
} from "./schemas"

export interface PaymentContextProps<T extends ZodSchema<any>> {
	formState: {
		payment_type: "self" | "loan"
		loan_payment_mode: "loan-disbursed" | "apply-loan"
		self_payment_mode: "pay-online" | "already-paid"
		payment_options: any[]
		selected_payment_option_id: string
		// Self Options
		order_id: string
		transaction_id: string
		payment_status: PaymentStatus
		payment_required: boolean
		paidAt: string
		// Loan Options
		loan_provider_id: string
		loan_provider_name: string
		loan_tenure: number
		required_fields: {
			// Changes based on loan provider
			[key: string]: string
		}
		loan_status: "DISBURED" | "REJECTED" | "PENDING"
		//total amount before discount
		total_amount: number
		//total amount after removing discount
		final_payable_amount: number
		stage_check_in_time: string
		hideDownloadReceipt: boolean
	}
	loanProviderForm: {
		form: UseFormReturn<z.infer<T>>
		defaultValues?: z.infer<T>
	}
	selfOfflinePaymentForm: {
		form: UseFormReturn<z.infer<typeof offlinePaymentSchema>>
		defaultValues?: z.infer<typeof offlinePaymentSchema>
	}
	loanOfflinePaymentForm: {
		form: UseFormReturn<z.infer<typeof loanOfflinePaymentSchema>>
		defaultValues?: z.infer<typeof loanOfflinePaymentSchema>
	}
	enableOfflinePayment: boolean
	paymentPlansPrices: FEE_GRID_RESPONSE | undefined
	fieldConfig: FIELD
	displayTheSelfAndLoanPaymentModesTogether: boolean
	activeLoanPlans: FEE_ITEM[]
	activeSelfPlans: FEE_ITEM[]
	feeSummary: FEE_ITEM[]
	applicatioDiscount?: ApplicationDiscount
	couponDiscount?: COUPON_DISCOUNT
	activeTab: string
	setActiveTab: (tab: string) => void
	getValues: () => any
	setValues: (values: any) => void
	validate: () => boolean
	selectedPaymentPlan: string
	setSelectedPaymentPlan: (plan: string) => void
	isPreviewMode: boolean
	course_id: string
	periodNumber: number
}

export const PaymentContext = createContext<PaymentContextProps<
	typeof combinedSchema
> | null>(null)

export const usePayment = () => {
	const context = useContext(PaymentContext)
	if (!context) {
		throw new Error("usePayment must be used within Payment.Provider")
	}
	return context
}
